<template>
    <v-dialog v-model="dialog" max-width="1250" scrollable :retain-focus="false">
        <v-card>
            <v-card-title class="barcode-head" :style="`direction:${lang.dir}`">
                <span>{{ t.barcode_print }}</span>
                <v-spacer></v-spacer>
                <span class="barcode-count">{{ t.items }}: {{ items.length }}</span>
                <v-btn icon small @click="close"><i class="fas fa-times"></i></v-btn>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text style="padding:0;">
                <v-row no-gutters :style="`direction:${lang.dir}`">
                    <!-- Options -->
                    <v-col cols="12" md="5" class="barcode-options">
                        <div class="opt-group">
                            <div class="opt-title">{{ t.paper_and_margins }}</div>
                            <v-row>
                                <v-col cols="6">
                                    <label>{{ t.measure_unit }}</label>
                                    <b-form-select class="selborder" v-model="opt.units" :options="unitOptions" />
                                </v-col>
                                <v-col cols="6">
                                    <label>{{ t.paper_size }}</label>
                                    <b-form-select class="selborder" v-model="opt.paper" :options="paperOptions" />
                                </v-col>
                                <v-col cols="6">
                                    <label>{{ t.orientation }}</label>
                                    <b-form-select class="selborder" v-model="opt.orientation" :options="orientationOptions" />
                                </v-col>
                                <v-col cols="6" v-if="opt.paper !== 'CUSTOM'"></v-col>
                                <v-col cols="6" v-if="opt.paper === 'CUSTOM'">
                                    <label>{{ t.page_width }} ({{ unitLabel }})</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0.5" v-model="opt.page_width" />
                                </v-col>
                                <v-col cols="6" v-if="opt.paper === 'CUSTOM'">
                                    <label>{{ t.page_height }} ({{ unitLabel }})</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0.5" v-model="opt.page_height" />
                                </v-col>
                                <v-col cols="3">
                                    <label>{{ t.margin_top }}</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0" v-model="opt.margin_top" />
                                </v-col>
                                <v-col cols="3">
                                    <label>{{ t.margin_bottom }}</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0" v-model="opt.margin_bottom" />
                                </v-col>
                                <v-col cols="3">
                                    <label>{{ t.margin_left }}</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0" v-model="opt.margin_left" />
                                </v-col>
                                <v-col cols="3">
                                    <label>{{ t.margin_right }}</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0" v-model="opt.margin_right" />
                                </v-col>
                            </v-row>
                        </div>

                        <div class="opt-group">
                            <div class="opt-title">{{ t.label_grid }}</div>
                            <v-row>
                                <v-col cols="3">
                                    <label>{{ t.columns }}</label>
                                    <b-form-input class="inborder" type="number" min="1" max="12" step="1" v-model="opt.cols" />
                                </v-col>
                                <v-col cols="3">
                                    <label>{{ t.rows }}</label>
                                    <b-form-input class="inborder" type="number" min="1" max="30" step="1" v-model="opt.rows" />
                                </v-col>
                                <v-col cols="3">
                                    <label>{{ t.gap_x }}</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0" v-model="opt.gap_x" />
                                </v-col>
                                <v-col cols="3">
                                    <label>{{ t.gap_y }}</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0" v-model="opt.gap_y" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.copies }}</label>
                                    <b-form-input class="inborder" type="number" min="1" max="200" step="1" v-model="opt.copies" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.font_size }} (pt)</label>
                                    <b-form-input class="inborder" type="number" min="3" max="40" step="0.5" v-model="opt.font_size" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.cut_guides }}</label>
                                    <b-form-select class="selborder" v-model="opt.show_border" :options="yesNoOptions" />
                                </v-col>
                            </v-row>
                            <div class="opt-note">{{ labelSizeNote }}</div>
                        </div>

                        <div class="opt-group">
                            <div class="opt-title">{{ t.barcode_settings }}</div>
                            <v-row>
                                <v-col cols="4">
                                    <label>{{ t.show_barcode }}</label>
                                    <b-form-select class="selborder" v-model="opt.show_barcode" :options="yesNoOptions" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.barcode_type }}</label>
                                    <b-form-select class="selborder" v-model="opt.barcode_type" :options="barcodeTypes" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.code_source }}</label>
                                    <b-form-select class="selborder" v-model="opt.code_source" :options="codeSources" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.barcode_width }} (%)</label>
                                    <b-form-input class="inborder" type="number" min="10" max="100" step="5" v-model="opt.barcode_width_pct" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.barcode_height }} ({{ unitLabel }})</label>
                                    <b-form-input class="inborder" type="number" :step="step" min="0.05" v-model="opt.barcode_height" />
                                </v-col>
                                <v-col cols="4">
                                    <label>{{ t.show_code_text }}</label>
                                    <b-form-select class="selborder" v-model="opt.show_barcode_text" :options="yesNoOptions" />
                                </v-col>
                            </v-row>
                            <div class="opt-note" :class="{'opt-warn': geometryWarning}">{{ barcodeNote }}</div>
                        </div>

                        <div class="opt-group">
                            <div class="opt-title">{{ t.what_to_print }}</div>
                            <div class="field-list">
                                <label v-for="field in fieldOptions" :key="field.value" class="field-item">
                                    <input type="checkbox" :value="field.value" v-model="selectedFields" />
                                    <span>{{ field.text }}</span>
                                </label>
                            </div>
                        </div>
                    </v-col>

                    <!-- Preview -->
                    <v-col cols="12" md="7" class="barcode-preview">
                        <div class="preview-bar">
                            <span>{{ t.preview }}</span>
                            <span class="preview-status" v-if="refreshing">{{ t.updating }}</span>
                        </div>
                        <iframe
                            v-if="previewUrl"
                            ref="preview"
                            :src="previewUrl"
                            class="preview-frame"
                            title="barcode-preview"
                        ></iframe>
                        <div v-else class="preview-empty">{{ t.no_preview }}</div>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions :style="`direction:${lang.dir}`">
                <b-button variant="secondary" class="btn-sm" style="width:110px;" @click="close">{{ lang.close }}</b-button>
                <v-spacer></v-spacer>
                <b-button variant="outline-primary" class="btn-sm" style="width:150px;" @click="refresh">{{ t.refresh_preview }}</b-button>
                <b-button variant="primary" class="btn-sm" style="width:140px;" @click="download">{{ t.download_pdf }}</b-button>
                <b-button variant="success" class="btn-sm" style="width:120px;" @click="printIt">{{ lang.print }}</b-button>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/**
 * Barcode label sheet designer.
 *
 * The PDF itself is built by /api/barcode-print.php; everything here turns the
 * chosen options into that endpoint's query string and shows the result in an
 * iframe, so what the user previews is exactly what prints. The same endpoint
 * with probe=1 returns the resulting geometry as JSON, which drives the size
 * readout under the options.
 *
 * All lengths are entered in the unit named by opt.units - inches by default.
 */
import axios from 'axios'

const STORE_KEY = 'ecar.barcodeLabelOptions';
// Bumped when the stored shape changes; anything older is ignored rather than
// half-restored. v1 held millimetre values and a raw bar-width multiplier.
const STORE_VERSION = 2;

const MM_PER_IN = 25.4;

// Every length below is in inches, which is the default unit.
const defaultOptions = () => ({
    units: 'in',
    paper: 'A4',
    orientation: 'P',
    page_width: 4,
    page_height: 2,
    margin_top: 0.25,
    margin_bottom: 0.25,
    margin_left: 0.25,
    margin_right: 0.25,
    cols: 3,
    rows: 8,
    gap_x: 0.1,
    gap_y: 0.1,
    copies: 1,
    font_size: 7,
    show_border: 0,
    show_barcode: 1,
    barcode_type: 'C128B',
    code_source: 'barcode',
    barcode_width_pct: 90,
    barcode_height: 0.4,
    show_barcode_text: 1,
});

// Paper sizes are held in millimetres and shown in the chosen unit.
const PAPERS = {
    A4: [210, 297],
    A5: [148, 210],
    A6: [105, 148],
    LETTER: [215.9, 279.4],
    LEGAL: [215.9, 355.6],
    LABEL_4X6: [101.6, 152.4],
    LABEL_4X2: [101.6, 50.8],
    LABEL_3X2: [76.2, 50.8],
    LABEL_225X125: [57.15, 31.75],
    LABEL_2X1: [50.8, 25.4],
    LABEL_100X50: [100, 50],
};

// Which option keys hold a length and therefore convert when the unit changes.
const LENGTH_KEYS = [
    'page_width', 'page_height',
    'margin_top', 'margin_bottom', 'margin_left', 'margin_right',
    'gap_x', 'gap_y', 'barcode_height',
];

export default {
    name: 'ProductBarcode',
    data() {
        return {
            dialog: false,
            items: [],
            opt: defaultOptions(),
            selectedFields: ['name_ar', 'item_code', 'price'],
            previewUrl: '',
            refreshing: false,
            timer: null,
            geometry: null,
            origin: window.location.origin,
        }
    },
    watch: {
        // Any option change re-renders the preview, debounced so typing in a
        // number field does not fire a request per keystroke.
        opt: {
            deep: true,
            handler() { this.schedule(); },
        },
        selectedFields() { this.schedule(); },
        // Switching units keeps the physical layout identical.
        'opt.units': function (next, prev) {
            if (!prev || next === prev) return;
            const factor = next === 'mm' ? MM_PER_IN : (1 / MM_PER_IN);
            const decimals = next === 'mm' ? 1 : 3;
            LENGTH_KEYS.forEach((key) => {
                const value = parseFloat(this.opt[key]);
                if (!isNaN(value)) {
                    this.opt[key] = parseFloat((value * factor).toFixed(decimals));
                }
            });
        },
    },
    computed: {
        lang: function(){
            if(this.$cookies.get(this.$store.state.LangCooki) != null){
                if(this.$cookies.get(this.$store.state.LangCooki)  == 'en'){
                    return this.$store.state.lang.en;
                }else if(this.$cookies.get(this.$store.state.LangCooki)  == 'ur'){
                    return this.$store.state.lang.ur;
                }else{
                    return this.$store.state.lang.ar;
                }
            }
            else if(this.$store.state.deflang == 'en'){
                return this.$store.state.lang.en;
            }
            else if(this.$store.state.deflang == 'ur'){
                return this.$store.state.lang.ur;
            }else{
                return this.$store.state.lang.ar;
            }
        },
        isArabic(){
            return this.lang.langname !== 'en';
        },
        t(){
            const l = this.lang || {};
            const ar = this.isArabic;
            return {
                barcode_print: l.barcode_print || (ar ? 'طباعة الباركود' : 'Print barcodes'),
                items: l.items || (ar ? 'الأصناف' : 'Items'),
                measure_unit: l.measure_unit || (ar ? 'وحدة القياس' : 'Unit'),
                inches: l.inches || (ar ? 'بوصة' : 'Inches'),
                millimetres: l.millimetres || (ar ? 'مليمتر' : 'Millimetres'),
                paper_and_margins: l.paper_and_margins || (ar ? 'الورق والهوامش' : 'Paper & margins'),
                paper_size: l.paper_size || (ar ? 'حجم الورق' : 'Paper size'),
                orientation: l.orientation || (ar ? 'الاتجاه' : 'Orientation'),
                portrait: l.portrait || (ar ? 'طولي' : 'Portrait'),
                landscape: l.landscape || (ar ? 'عرضي' : 'Landscape'),
                page_width: l.page_width || (ar ? 'العرض' : 'Width'),
                page_height: l.page_height || (ar ? 'الارتفاع' : 'Height'),
                margin_top: l.margin_top || (ar ? 'هامش أعلى' : 'Top'),
                margin_bottom: l.margin_bottom || (ar ? 'هامش أسفل' : 'Bottom'),
                margin_left: l.margin_left || (ar ? 'هامش يسار' : 'Left'),
                margin_right: l.margin_right || (ar ? 'هامش يمين' : 'Right'),
                label_grid: l.label_grid || (ar ? 'توزيع الملصقات' : 'Label grid'),
                columns: l.columns || (ar ? 'أعمدة' : 'Columns'),
                rows: l.rows || (ar ? 'صفوف' : 'Rows'),
                gap_x: l.gap_x || (ar ? 'فاصل أفقي' : 'Gap X'),
                gap_y: l.gap_y || (ar ? 'فاصل رأسي' : 'Gap Y'),
                copies: l.label_copies || (ar ? 'عدد النسخ' : 'Copies'),
                font_size: l.font_size || (ar ? 'حجم الخط' : 'Font size'),
                cut_guides: l.cut_guides || (ar ? 'خطوط القص' : 'Cut guides'),
                barcode_settings: l.barcode_settings || (ar ? 'إعدادات الباركود' : 'Barcode'),
                show_barcode: l.show_barcode || (ar ? 'إظهار الباركود' : 'Show barcode'),
                barcode_type: l.barcode_type || (ar ? 'نوع الباركود' : 'Symbology'),
                code_source: l.code_source || (ar ? 'مصدر الرقم' : 'Encode field'),
                barcode_width: l.barcode_width_share || (ar ? 'عرض الباركود من الملصق' : 'Width of label'),
                barcode_height: l.barcode_height || (ar ? 'ارتفاع الباركود' : 'Bar height'),
                show_code_text: l.show_code_text || (ar ? 'إظهار الرقم' : 'Show code text'),
                what_to_print: l.what_to_print || (ar ? 'ما الذي يطبع على الملصق' : 'What to print'),
                preview: l.preview || (ar ? 'معاينة' : 'Preview'),
                updating: l.updating || (ar ? 'جاري التحديث...' : 'Updating...'),
                no_preview: l.no_preview || (ar ? 'لا توجد معاينة' : 'No preview'),
                refresh_preview: l.refresh_preview || (ar ? 'تحديث المعاينة' : 'Refresh preview'),
                download_pdf: l.download_pdf || (ar ? 'تحميل PDF' : 'Download PDF'),
                label_size: l.label_size || (ar ? 'مقاس الملصق' : 'Label size'),
                per_page: l.per_page || (ar ? 'في الصفحة' : 'per page'),
                barcode_size_is: l.barcode_size_is || (ar ? 'مقاس الباركود' : 'Barcode'),
                too_fine: l.barcode_too_fine || (ar
                    ? 'الأعمدة رفيعة جدا وقد لا تقرأ - كبر عرض الملصق أو قلل طول الرقم'
                    : 'bars are too fine to scan reliably - widen the label or shorten the code'),
                wont_fit: l.barcode_wont_fit || (ar ? 'الباركود أعرض من الملصق' : 'the barcode is wider than the label'),
                encoded_as: l.encoded_as || (ar
                    ? 'تنبيه: سيتم ترميز الرقم هكذا'
                    : 'Warning: the bars will actually encode'),
                invalid_codes: l.invalid_codes || (ar
                    ? 'أصناف لا يقبلها نوع الباركود المختار'
                    : 'item(s) whose code does not fit the chosen symbology'),
                company: l.company_name || (ar ? 'اسم المنشأة' : 'Company name'),
                name_ar: l.product_namear || (ar ? 'الاسم العربي' : 'Name (AR)'),
                name_en: l.product_nameen || (ar ? 'الاسم الإنجليزي' : 'Name (EN)'),
                item_code: l.item_code || (ar ? 'رمز الصنف' : 'Item code'),
                internal_code: l.internal_code || (ar ? 'الرمز الداخلي' : 'Internal code'),
                brand: l.brand_name || (ar ? 'الماركة' : 'Brand'),
                category: l.category_name || (ar ? 'القسم' : 'Category'),
                price: l.item_price || (ar ? 'السعر' : 'Price'),
                vat_price: l.price_with_vat || (ar ? 'السعر شامل الضريبة' : 'Price incl. VAT'),
                barcode_value: l.barcode || (ar ? 'الباركود' : 'Barcode'),
                yes: l.yes || (ar ? 'نعم' : 'Yes'),
                no: l.no || (ar ? 'لا' : 'No'),
            }
        },
        unitLabel(){
            return this.opt.units === 'mm' ? (this.isArabic ? 'مم' : 'mm') : (this.isArabic ? 'بوصة' : 'in');
        },
        step(){
            return this.opt.units === 'mm' ? '1' : '0.05';
        },
        decimals(){
            return this.opt.units === 'mm' ? 1 : 2;
        },
        unitOptions(){
            return [
                { text: this.t.inches, value: 'in' },
                { text: this.t.millimetres, value: 'mm' },
            ]
        },
        paperOptions(){
            const label = (key, name) => {
                const mm = PAPERS[key];
                const w = this.fromMm(mm[0]);
                const h = this.fromMm(mm[1]);
                return { text: `${name} (${w} x ${h} ${this.unitLabel})`, value: key };
            };
            return [
                label('A4', 'A4'),
                label('A5', 'A5'),
                label('A6', 'A6'),
                label('LETTER', 'Letter'),
                label('LEGAL', 'Legal'),
                label('LABEL_4X6', this.isArabic ? 'ملصق' : 'Label'),
                label('LABEL_4X2', this.isArabic ? 'ملصق' : 'Label'),
                label('LABEL_3X2', this.isArabic ? 'ملصق' : 'Label'),
                label('LABEL_225X125', this.isArabic ? 'ملصق' : 'Label'),
                label('LABEL_2X1', this.isArabic ? 'ملصق' : 'Label'),
                label('LABEL_100X50', this.isArabic ? 'ملصق' : 'Label'),
                { text: this.isArabic ? 'مقاس مخصص' : 'Custom size', value: 'CUSTOM' },
            ]
        },
        orientationOptions(){
            return [
                { text: this.t.portrait, value: 'P' },
                { text: this.t.landscape, value: 'L' },
            ]
        },
        yesNoOptions(){
            return [
                { text: this.t.yes, value: 1 },
                { text: this.t.no, value: 0 },
            ]
        },
        barcodeTypes(){
            return [
                { text: 'Code 128 B (text + digits)', value: 'C128B' },
                { text: 'Code 128 A', value: 'C128A' },
                { text: 'Code 128 C (digits, even count)', value: 'C128C' },
                { text: 'Code 39', value: 'C39' },
                { text: 'Code 39 + checksum', value: 'C39+' },
                { text: 'Code 93', value: 'C93' },
                { text: 'EAN-13 (12-13 digits)', value: 'EAN13' },
                { text: 'EAN-8 (7-8 digits)', value: 'EAN8' },
                { text: 'UPC-A (11-12 digits)', value: 'UPCA' },
                { text: 'Interleaved 2 of 5', value: 'I25' },
                { text: 'Codabar', value: 'CODABAR' },
            ]
        },
        codeSources(){
            return [
                { text: this.t.barcode_value, value: 'barcode' },
                { text: this.t.item_code, value: 'item_code' },
                { text: 'GTIN', value: 'gtin' },
                { text: 'MPN', value: 'mpn' },
                { text: this.t.internal_code, value: 'internal_code' },
            ]
        },
        fieldOptions(){
            return [
                { text: this.t.company, value: 'company' },
                { text: this.t.name_ar, value: 'name_ar' },
                { text: this.t.name_en, value: 'name_en' },
                { text: this.t.item_code, value: 'item_code' },
                { text: this.t.internal_code, value: 'internal_code' },
                { text: this.t.brand, value: 'brand' },
                { text: this.t.category, value: 'category' },
                { text: 'MPN', value: 'mpn' },
                { text: 'GTIN', value: 'gtin' },
                { text: this.t.barcode_value, value: 'barcode_value' },
                { text: this.t.price, value: 'price' },
                { text: this.t.vat_price, value: 'vat_price' },
            ]
        },
        /** Label size worked out locally so it updates as the user types. */
        labelSizeNote(){
            const size = PAPERS[this.opt.paper]
                ? PAPERS[this.opt.paper].map((mm) => this.fromMm(mm))
                : [this.num('page_width'), this.num('page_height')];
            let w = size[0];
            let h = size[1];
            if(this.opt.orientation === 'L'){ const s = w; w = h; h = s; }

            const cols = Math.max(1, parseInt(this.opt.cols) || 1);
            const rows = Math.max(1, parseInt(this.opt.rows) || 1);
            const usableW = w - this.num('margin_left') - this.num('margin_right');
            const usableH = h - this.num('margin_top') - this.num('margin_bottom');
            const lw = (usableW - (cols - 1) * this.num('gap_x')) / cols;
            const lh = (usableH - (rows - 1) * this.num('gap_y')) / rows;

            if(!(lw > 0) || !(lh > 0)){
                return this.isArabic
                    ? 'المقاس الحالي لا يتسع في الصفحة'
                    : 'These settings do not fit on the page';
            }

            const d = this.decimals;
            return `${this.t.label_size}: ${lw.toFixed(d)} x ${lh.toFixed(d)} ${this.unitLabel}`
                + ` - ${cols * rows} ${this.t.per_page}`;
        },
        /** Reported by the server, so it reflects the real encoded symbol. */
        barcodeNote(){
            if(this.opt.show_barcode == 0) return '';
            const g = this.geometry;
            if(!g) return '';

            const parts = [];
            if(g.barcode){
                const d = this.decimals;
                parts.push(`${this.t.barcode_size_is}: ${g.barcode.width.toFixed(d)}`
                    + ` x ${g.barcode.height.toFixed(d)} ${this.unitLabel}`);
                if(g.barcode.overflows_label){
                    parts.push(this.t.wont_fit);
                }else if(!g.barcode.scannable){
                    parts.push(this.t.too_fine);
                }
            }
            if(g.rewritten){
                parts.push(`${this.t.encoded_as}: ${g.rewritten.from} → ${g.rewritten.to}`);
            }
            if(g.invalid_count){
                parts.push(`${g.invalid_count} ${this.t.invalid_codes}`);
            }
            return parts.join(' - ');
        },
        geometryWarning(){
            const g = this.geometry;
            if(!g) return false;
            return !!(g.invalid_count || g.rewritten
                || (g.barcode && (!g.barcode.scannable || g.barcode.overflows_label)));
        },
    },
    methods: {
        /** A stored millimetre value shown in the current unit. */
        fromMm(mm){
            const value = this.opt.units === 'mm' ? mm : mm / MM_PER_IN;
            return parseFloat(value.toFixed(this.opt.units === 'mm' ? 1 : 2));
        },
        num(key){
            const value = parseFloat(this.opt[key]);
            return isNaN(value) ? 0 : value;
        },
        /**
         * @param {Array|Object} items  product rows straight off the list.
         */
        open(items){
            const list = Array.isArray(items) ? items : [items];
            this.items = list.filter((item) => item && item.id);
            this.restoreOptions();
            this.geometry = null;
            this.dialog = true;
            this.refresh();
        },
        close(){
            this.dialog = false;
            this.previewUrl = '';
            if(this.timer) clearTimeout(this.timer);
        },
        restoreOptions(){
            try {
                const saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
                if(saved && typeof saved === 'object' && saved.v === STORE_VERSION){
                    this.opt = Object.assign(defaultOptions(), saved.opt || {});
                    if(Array.isArray(saved.fields) && saved.fields.length){
                        this.selectedFields = saved.fields;
                    }
                }else{
                    this.opt = defaultOptions();
                }
            } catch (e) {
                this.opt = defaultOptions();
            }
        },
        saveOptions(){
            try {
                localStorage.setItem(STORE_KEY, JSON.stringify({
                    v: STORE_VERSION,
                    opt: this.opt,
                    fields: this.selectedFields,
                }));
            } catch (e) {
                // A full or blocked localStorage must not stop the preview.
            }
        },
        schedule(){
            if(!this.dialog) return;
            this.refreshing = true;
            if(this.timer) clearTimeout(this.timer);
            this.timer = setTimeout(() => this.refresh(), 500);
        },
        buildUrl(extra){
            const params = Object.assign({
                auth: this.$cookies.get(this.$store.state.COOKIEPhase),
                defBranch: localStorage.getItem('defBranch') || 1,
                lang: this.lang.langname === 'en' ? 'en' : 'ar',
                items: this.items.map((item) => item.id).join(','),
                fields: this.selectedFields.join(','),
                units: this.opt.units,
                paper: this.opt.paper,
                orientation: this.opt.orientation,
                page_width: this.opt.page_width,
                page_height: this.opt.page_height,
                margin_top: this.opt.margin_top,
                margin_bottom: this.opt.margin_bottom,
                margin_left: this.opt.margin_left,
                margin_right: this.opt.margin_right,
                cols: this.opt.cols,
                rows: this.opt.rows,
                gap_x: this.opt.gap_x,
                gap_y: this.opt.gap_y,
                copies: this.opt.copies,
                font_size: this.opt.font_size,
                show_border: this.opt.show_border,
                show_barcode: this.opt.show_barcode,
                barcode_type: this.opt.barcode_type,
                code_source: this.opt.code_source,
                barcode_width_pct: this.opt.barcode_width_pct,
                barcode_height: this.opt.barcode_height,
                show_barcode_text: this.opt.show_barcode_text,
                // Busts the iframe cache so each refresh really re-renders.
                _: Date.now(),
            }, extra || {});

            const query = Object.keys(params)
                .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
                .join('&');

            return this.origin + '/api/barcode-print.php?' + query;
        },
        refresh(){
            if(this.timer) clearTimeout(this.timer);
            if(!this.items.length){
                this.previewUrl = '';
                this.refreshing = false;
                return;
            }
            this.saveOptions();
            this.previewUrl = this.buildUrl();
            this.refreshing = false;
            this.fetchGeometry();
        },
        /** Asks the same endpoint what the labels and bars actually measure. */
        fetchGeometry(){
            axios.get(this.buildUrl({ probe: 1 }))
                .then((response) => {
                    this.geometry = (response && response.data && typeof response.data === 'object')
                        ? response.data : null;
                })
                .catch(() => { this.geometry = null; });
        },
        printIt(){
            if(!this.items.length) return;
            const frame = this.$refs.preview;
            // The preview is same-origin, so it can be printed directly. If the
            // browser blocks that, fall back to opening the PDF in a tab.
            try {
                if(frame && frame.contentWindow){
                    frame.contentWindow.focus();
                    frame.contentWindow.print();
                    return;
                }
            } catch (e) {
                // fall through
            }
            window.open(this.buildUrl(), '_blank');
        },
        download(){
            if(!this.items.length) return;
            window.open(this.buildUrl({ download: 1 }), '_blank');
        },
    },
    beforeDestroy(){
        if(this.timer) clearTimeout(this.timer);
    },
}
</script>

<style scoped>
.barcode-head {
    font-size: 16px;
    padding: 10px 16px;
}
.barcode-count {
    font-size: 12px;
    color: #666;
    margin-inline-end: 10px;
}
.barcode-options {
    padding: 12px;
    max-height: 70vh;
    overflow-y: auto;
    border-inline-end: 1px solid #eee;
}
.opt-group {
    margin-bottom: 14px;
}
.opt-title {
    font-weight: 700;
    font-size: 13px;
    background: #f3f3f3;
    padding: 4px 8px;
    border-radius: 4px;
    margin-bottom: 6px;
}
.opt-note {
    font-size: 12px;
    color: #555;
    margin-top: 6px;
}
.opt-note.opt-warn {
    color: #b00;
    font-weight: 600;
}
.barcode-options label {
    font-size: 12px;
    margin-bottom: 2px;
    display: block;
}
.field-list {
    display: flex;
    flex-wrap: wrap;
}
.field-item {
    width: 50%;
    font-size: 12px;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}
.field-item input {
    margin-inline-end: 6px;
}
.barcode-preview {
    display: flex;
    flex-direction: column;
    background: #525659;
    min-height: 70vh;
}
.preview-bar {
    display: flex;
    justify-content: space-between;
    color: #fff;
    font-size: 12px;
    padding: 6px 10px;
}
.preview-frame {
    flex: 1;
    width: 100%;
    border: none;
    min-height: 65vh;
}
.preview-empty {
    color: #ddd;
    text-align: center;
    padding-top: 40px;
}
</style>
